import React, { useRef, useState } from "react";
import { Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  CertificateSignature,
  DEFAULT_SIGNATURE_NAME,
} from "../certificates/certificateData";

interface SignatureControlsProps {
  /** Written on every change; the page reads it at certificate-print time. */
  signatureRef: React.MutableRefObject<CertificateSignature>;
}

/**
 * Signer controls for certificates: the printed name line and an optional
 * PNG signature image. Held in memory for this session only — never uploaded
 * or persisted anywhere. Owns its own state and publishes through
 * `signatureRef` so typing here never re-renders the leaderboards.
 */
const SignatureControls: React.FC<SignatureControlsProps> = ({
  signatureRef,
}) => {
  const [name, setName] = useState(DEFAULT_SIGNATURE_NAME);
  const [image, setImage] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Monotonic token identifying the most recent upload/clear action. Reading a
  // file and decode-probing it are both async, so on rapid re-uploads an older
  // probe can resolve after a newer one. Each async continuation captures the
  // token it started with and applies its result only while still the latest —
  // so the newest selection always wins and a cleared image can't be revived.
  const uploadTokenRef = useRef(0);

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
    signatureRef.current = {
      ...signatureRef.current,
      name: event.target.value,
    };
  };

  const setSignatureImage = (dataUrl: string | undefined) => {
    setImage(dataUrl ?? null);
    signatureRef.current = { ...signatureRef.current, image: dataUrl };
  };

  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset so re-selecting the same file fires onChange again.
    event.target.value = "";
    if (!file) {
      return;
    }
    // Claim the latest-upload token; any earlier in-flight read/probe is now
    // stale and its result will be ignored.
    const token = ++uploadTokenRef.current;
    const isStale = () => token !== uploadTokenRef.current;
    setUploadError(null);
    // `accept` is only a picker hint — a user can still choose anything. Reject
    // up front so a bad file never reaches the ref and poisons every later
    // certificate render. @react-pdf/renderer decodes only PNG and JPEG, so a
    // GIF/WEBP/BMP/SVG — even with a correct image/* MIME — would break the
    // render; restrict to the two formats react-pdf can actually embed.
    if (file.type !== "image/png" && file.type !== "image/jpeg") {
      setUploadError("Please choose a PNG or JPEG image.");
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => {
      if (isStale()) {
        return;
      }
      setUploadError("Couldn't read that file. Please try again.");
    };
    reader.onload = () => {
      if (isStale()) {
        return;
      }
      const dataUrl = reader.result;
      if (typeof dataUrl !== "string") {
        setUploadError("Couldn't read that file. Please try again.");
        return;
      }
      // A correct MIME type can still wrap corrupt/mislabeled bytes; confirm the
      // data actually decodes as an image before storing it.
      const probe = new Image();
      probe.onload = () => {
        if (isStale()) {
          return;
        }
        setUploadError(null);
        setSignatureImage(dataUrl);
      };
      probe.onerror = () => {
        if (isStale()) {
          return;
        }
        setUploadError("That file isn't a valid image.");
      };
      probe.src = dataUrl;
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="ml-auto flex flex-wrap items-end gap-4">
      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-gray-500 dark:text-slate-400">
          Signature Name
        </span>
        <Input
          className="w-[250px]"
          onChange={handleNameChange}
          placeholder={DEFAULT_SIGNATURE_NAME}
          value={name}
        />
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-gray-500 dark:text-slate-400">
          Signature Image
        </span>
        {image ? (
          <div className="flex items-center gap-2">
            <img
              alt="Signature preview"
              className="h-9 max-w-[150px] rounded border border-gray-200 bg-white object-contain px-1.5 dark:border-slate-600"
              src={image}
            />
            <Button
              aria-label="Remove signature image"
              onClick={() => {
                // Invalidate any in-flight read/probe so it can't resurrect the
                // image the user just cleared.
                uploadTokenRef.current += 1;
                setUploadError(null);
                setSignatureImage(undefined);
              }}
              size="sm"
              variant="ghost"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        ) : (
          <Button
            onClick={() => fileInputRef.current?.click()}
            variant="outline"
          >
            <Upload className="mr-1.5 h-4 w-4" />
            Upload signature
          </Button>
        )}
        {uploadError && (
          <span className="text-xs text-red-600 dark:text-red-400">
            {uploadError}
          </span>
        )}
        <input
          accept="image/png,image/jpeg"
          className="hidden"
          onChange={handleUpload}
          ref={fileInputRef}
          type="file"
        />
      </div>
    </div>
  );
};

export default SignatureControls;
